import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Lock, Users, BookOpen, Check } from "lucide-react";
import { api } from "../lib/api";

/**
 * UserAccessModal — admin edits which courses a specific user can access.
 * Class-inherited courses are shown checked and locked (change via course tab).
 */
export function UserAccessModal({ open, user, onClose, onSaved }) {
  const [access, setAccess] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    Promise.all([
      api.get(`/admin/users/${user.id}/access`).then((r) => r.data),
      api.get("/courses").then((r) => r.data),
    ])
      .then(([accessData, allCourses]) => {
        setAccess(accessData);
        setCourses(allCourses);
        setSelected(new Set(accessData.authorized_courses || []));
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load access"));
  }, [open, user?.id]);

  const inheritedSet = useMemo(
    () => new Set((access?.inherited_from_class) || []),
    [access]
  );

  if (!open || !user) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${user.id}/access`, {
        authorized_courses: [...selected],
      });
      toast.success("Access updated — user auto-enrolled in new courses.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brutal-card p-6 bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-access-modal">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div>
            <div className="brutal-badge bg-brand-blue text-white text-xs mb-2">
              <Lock className="w-3.5 h-3.5" strokeWidth={3} /> Course access
            </div>
            <h3 className="font-display text-2xl font-bold text-brand-navy">{user.name}</h3>
            <p className="text-sm text-brand-navy/60">
              {user.email}
              {access?.class_year != null && (
                <span className="ml-2 brutal-badge bg-brand-cream text-xs">Class {access.class_year}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-cream rounded-full shrink-0" data-testid="user-access-close">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        {!access && <p className="text-brand-navy/60">Loading…</p>}

        {access && (
          <>
            <p className="text-xs text-brand-navy/70 mb-3">
              Check the courses this user can see and study. Class-inherited courses are locked (edit on the course itself).
            </p>
            <div className="space-y-2 mb-4">
              {courses.map((c) => {
                const inherited = inheritedSet.has(c.id);
                const checked = selected.has(c.id) || inherited;
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 brutal-card p-3 cursor-pointer transition-all ${checked ? "bg-brand-yellow" : "bg-white hover:-translate-y-0.5"} ${inherited ? "opacity-70" : ""}`}
                    data-testid={`user-access-row-${c.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={inherited}
                      onChange={() => toggle(c.id)}
                      className="w-5 h-5"
                    />
                    <div className="text-xl">{c.flag || "📚"}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-brand-navy truncate">{c.title}</div>
                      <div className="text-xs text-brand-navy/60">{c.language} · {c.card_count} cards</div>
                    </div>
                    {inherited && (
                      <span className="brutal-badge bg-brand-mint text-xs" title="Granted through class year">Class</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="brutal-btn-white text-sm py-2">Cancel</button>
              <button onClick={save} disabled={busy} className="brutal-btn-pink text-sm py-2" data-testid="user-access-save">
                <Check className="w-4 h-4" strokeWidth={3} /> {busy ? "Saving…" : "Save access"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * CourseAccessModal — admin edits which classes + individual users can access a course.
 * Auto-enrolls newly authorized users.
 */
export function CourseAccessModal({ open, course, onClose, onSaved }) {
  const [access, setAccess] = useState(null);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassYears, setSelectedClassYears] = useState(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !course?.id) return;
    Promise.all([
      api.get(`/admin/courses/${course.id}/access`).then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
      api.get("/admin/classes").then((r) => r.data),
    ])
      .then(([accessData, u, cls]) => {
        setAccess(accessData);
        setUsers(u);
        setClasses(cls.classes || []);
        setSelectedClassYears(new Set(accessData.class_years || []));
        setSelectedUserIds(new Set((accessData.authorized_users || []).map((x) => x.id)));
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load access"));
  }, [open, course?.id]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => (u.email + " " + u.name).toLowerCase().includes(needle));
  }, [users, q]);

  if (!open || !course) return null;

  const toggleYear = (y) => {
    setSelectedClassYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y);
      else next.add(y);
      return next;
    });
  };
  const toggleUser = (id) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/courses/${course.id}/access`, {
        class_years: [...selectedClassYears],
        authorized_user_ids: [...selectedUserIds],
      });
      const added = (data.added_grants || []).length;
      toast.success(`Access saved. ${added ? added + " new user(s) auto-enrolled." : ""}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="brutal-card p-6 bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="course-access-modal">
        <div className="flex items-start justify-between mb-3 gap-4">
          <div>
            <div className="brutal-badge bg-brand-blue text-white text-xs mb-2">
              <Lock className="w-3.5 h-3.5" strokeWidth={3} /> Course access
            </div>
            <h3 className="font-display text-2xl font-bold text-brand-navy">{course.title}</h3>
            <p className="text-sm text-brand-navy/60">{course.language}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-cream rounded-full shrink-0" data-testid="course-access-close">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        {!access && <p className="text-brand-navy/60">Loading…</p>}

        {access && (
          <>
            <section className="mb-4">
              <h4 className="font-display font-bold text-brand-navy text-sm mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" strokeWidth={3} /> Assign to classes
              </h4>
              {classes.length === 0 ? (
                <p className="text-xs text-brand-navy/60">No classes exist yet. Assign a class year to users first.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {classes.map((c) => (
                    <button
                      key={c.class_year}
                      type="button"
                      onClick={() => toggleYear(c.class_year)}
                      data-testid={`course-access-class-${c.class_year}`}
                      className={`brutal-badge text-xs ${selectedClassYears.has(c.class_year) ? "bg-brand-pink text-white" : "bg-white"}`}
                    >
                      Class {c.class_year} ({c.user_count})
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h4 className="font-display font-bold text-brand-navy text-sm mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4" strokeWidth={3} /> Individual users
              </h4>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="course-access-search"
                className="brutal-input mb-2 text-sm"
                placeholder="Search by name or email…"
              />
              <div className="brutal-card bg-white max-h-64 overflow-y-auto divide-y-2 divide-brand-ink/10">
                {filtered.map((u) => {
                  const inheritedViaClass = u.class_year != null && selectedClassYears.has(u.class_year);
                  const checked = selectedUserIds.has(u.id) || inheritedViaClass;
                  return (
                    <label key={u.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${inheritedViaClass ? "opacity-70" : ""}`} data-testid={`course-access-user-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={inheritedViaClass}
                        onChange={() => toggleUser(u.id)}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-brand-navy text-sm truncate">{u.name}</div>
                        <div className="text-xs text-brand-navy/60">{u.email}{u.class_year != null && ` · Class ${u.class_year}`}</div>
                      </div>
                      {inheritedViaClass && <span className="brutal-badge bg-brand-mint text-xs">Class</span>}
                    </label>
                  );
                })}
              </div>
            </section>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="brutal-btn-white text-sm py-2">Cancel</button>
              <button onClick={save} disabled={busy} className="brutal-btn-pink text-sm py-2" data-testid="course-access-save">
                <Check className="w-4 h-4" strokeWidth={3} /> {busy ? "Saving…" : "Save access"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
